/**
 * Back in stock notify-me popup (Klaviyo API).
 * @see https://help.backinstock.org/article/2303-javascript-api
 */
class NotifyMe extends HTMLElement {
  static BIS_API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  static BIS_REVISION = '2024-10-15';

  constructor() {
    super();
    this.selectedVariantId = null;
    this.formId = '';
    this.form = null;
    this.companyId = '';
    this.trigger = null;
    this.buttonSubmit = null;
    this.email = null;
    this.variantInput = null;
    this.dialog = null;
    this.dialogContainer = null;
    this.dialogNotify = null;
    this.emailError = null;
    this.closeDialogButton = null;
    this.notifyTitle = null;
    this.notifyTitleSuccess = null;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.abortController = null;
  }

  connectedCallback() {
    this.formId = this.dataset.formId || this.closest('buy-buttons')?.getAttribute('form') || '';
    this.companyId = this.dataset.companyId || '';
    this.bindElements();
    this.bindForm();
    this.addEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  bindElements() {
    this.trigger = this.querySelector('[data-notify-trigger]');
    this.buttonSubmit = this.querySelector('[data-notify-submit]');
    this.email = this.querySelector('[data-notify-email]');
    this.variantInput = this.querySelector('[data-notify-variant]');
    this.dialog = this.querySelector('[data-notify-dialog]');
    this.dialogContainer = this.querySelector('[data-notify-body]');
    this.dialogNotify = this.querySelector('[data-notify-success-panel]');
    this.emailError = this.querySelector('[data-notify-error]');
    this.closeDialogButton = this.querySelector('[data-notify-close]');
    this.notifyTitle = this.querySelector('[data-notify-title]');
    this.notifyTitleSuccess = this.querySelector('[data-notify-title-success]');
    this.selectedVariantId = this.variantInput?.value || null;
  }

  bindForm() {
    this.form = this.formId ? document.forms[this.formId] : null;
  }

  addEvents() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.trigger?.addEventListener('click', (event) => this.openNotifyDialog(event), { signal });
    this.buttonSubmit?.addEventListener('click', (event) => this.submitNotify(event), { signal });
    this.email?.addEventListener('click', () => this.clearEmailError(), { signal });
    this.closeDialogButton?.addEventListener('click', this.closeDialog, { signal });
    this.dialog?.addEventListener('click', this.closeDialog, { signal });

    this.form?.addEventListener('variant:change', this.onVariantChange, { signal });
  }

  /**
   * @param {CustomEvent} event
   */
  onVariantChange(event) {
    const variant = event.detail?.variant;

    if (!variant?.id) {
      return;
    }

    this.selectedVariantId = String(variant.id);
    this.variantInput && (this.variantInput.value = this.selectedVariantId);
    this.toggleAttribute('visible', variant.available === false);
  }

  /**
   * @returns {string|null}
   */
  getBisApiUrl() {
    if (!this.companyId) {
      return null;
    }

    return `${NotifyMe.BIS_API}?company_id=${encodeURIComponent(this.companyId)}`;
  }

  /**
   * Opens the notify-me dialog.
   */
  openDialog() {
    if (!this.selectedVariantId) {
      return;
    }

    this.dialogContainer?.classList.remove('hidden');
    this.dialogNotify?.classList.add('hidden');
    this.notifyTitle?.classList.remove('hidden');
    this.notifyTitleSuccess?.classList.add('hidden');
    !this.dialog?.open && this.dialog?.showModal();
  }

  /**
   * @param {Event} event
   */
  openNotifyDialog(event) {
    event.preventDefault();
    this.openDialog();
  }

  closeDialog(event) {
    if (event.target.closest('[data-notify-close], [data-notify-success-panel] button')) {
      this.dialog?.close();
      return;
    }

    event.target === this.dialog && this.dialog.close();
  }

  clearEmailError() {
    const field = this.email?.closest('.field_email');

    if (!field?.classList.contains('field--error')) {
      return;
    }

    field.classList.remove('field--error');
    this.emailError?.classList.add('hidden');
  }

  /**
   * @param {Event} event
   */
  async submitNotify(event) {
    event.preventDefault();

    if (this.buttonSubmit?.getAttribute('aria-busy') === 'true') {
      return;
    }

    this.buttonSubmit?.setAttribute('aria-busy', 'true');
    await this.createBIS();
    this.buttonSubmit?.removeAttribute('aria-busy');
  }

  async createBIS() {
    const apiUrl = this.getBisApiUrl();

    if (!apiUrl || !this.selectedVariantId) {
      this.emailError?.classList.remove('hidden');
      return;
    }

    const email = this.email?.value?.trim();

    if (!email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      this.email?.closest('.field_email')?.classList.add('field--error');
      this.emailError?.classList.remove('hidden');
      return;
    }

    this.email?.closest('.field_email')?.classList.remove('field--error');
    this.emailError?.classList.add('hidden');

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: NotifyMe.BIS_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.selectedVariantId}`,
                },
              },
            },
          },
        }),
      });

      if (!response.ok) {
        this.emailError?.classList.remove('hidden');
        return;
      }
    } catch {
      this.emailError?.classList.remove('hidden');
      return;
    }

    this.notificationCallback();
    typeof fbq === 'function' && fbq('track', 'CompleteRegistration');
  }

  notificationCallback() {
    this.classList.add('notification');
    this.dialogContainer?.classList.add('hidden');
    this.dialogNotify?.classList.remove('hidden');
    this.notifyTitle?.classList.add('hidden');
    this.notifyTitleSuccess?.classList.remove('hidden');
    this.email && (this.email.value = '');
  }
}

if (!window.customElements.get('notify-me')) {
  window.customElements.define('notify-me', NotifyMe);
}
